import { ref, computed, type Ref } from 'vue'
import { defineStore } from 'pinia'
import { OnboardingStatus, ResponseError } from '@roadfootage/api-client'
import type {
  TermsStatusDto,
  UserProfileDetailsDto,
  UserProfileDto,
  UserPreferencesDto,
  AccountDeletionDto,
  Operation
} from '@roadfootage/api-client'
import type { Operation as JsonPatchOperation } from 'fast-json-patch'

import logger from '../../logger'
import { useApi } from '../../composables/api'
import { useWalletStore } from '../wallet'
import { readApiErrorMessage } from '../../utils/api-error'

const TERMS_ERROR = 'Could not save your acceptance. Please try again.'

export const useUserStore = defineStore('user', () => {
  const userProfile: Ref<UserProfileDto | null> = ref(null)
  const userPreferences: Ref<UserPreferencesDto | null> = ref(null)

  const accountDeletion: Ref<AccountDeletionDto | null> = ref(null)

  // The terms answer is shown inline by the Updated terms screen and the wizard, so no global toast.
  const terms: Ref<TermsStatusDto | null> = ref(null)
  const termsError: Ref<string | null> = ref(null)
  // Set by the api middleware when a write is refused for old terms (race: the user was current when /me loaded).
  const termsRefused = ref(false)

  const { meApi } = useApi()
  const { meApi: quietMeApi } = useApi({ notifyOnError: false })

  const isProfileSetUp = computed(() => userProfile.value !== null)
  const isPreferencesSetUp = computed(() => userPreferences.value !== null)
  const termsUpdateRequired = computed(
    () => termsRefused.value || terms.value?.updateRequired === true
  )
  // Staff banned this account: the API refuses its writes (403 account_restricted), so the app hides the entry points.
  const isRestricted = computed(() => userProfile.value?.isBanned === true)

  async function loadProfile(): Promise<boolean> {
    userProfile.value = null

    try {
      const response = await meApi.meFetchMe()
      console.log('Loaded profile', response)
      userProfile.value = response.userProfile ?? null
      userPreferences.value = response.userPreferences ?? null
      accountDeletion.value = response.accountDeletion ?? null
      terms.value = response.terms ?? null
      termsRefused.value = false
      useWalletStore().setWallet(response.userWallet)
      if (response.status !== OnboardingStatus.Complete) return false
    } catch (e) {
      logger.error(e, 'Failed to load profile')
      throw e
    }
    return true
  }

  async function createProfile(
    userProfileDetailsDto: UserProfileDetailsDto = {},
    turnstileToken: string
  ) {
    userProfile.value = await meApi.meCreateUserProfile({
      userProfileDetailsDto,
      turnstileToken
    })
  }

  async function updateProfile(patchOperations: JsonPatchOperation[]) {
    userProfile.value = await meApi.meUpdateUserProfileDetails({
      operation: patchOperations as Operation[]
    })
  }

  async function savePreferences(userPreferencesDto: UserPreferencesDto = {}) {
    userPreferences.value = await meApi.meSetUserPreferences({
      userPreferencesDto
    })
  }

  async function requestAccountDeletion() {
    accountDeletion.value = await meApi.meRequestAccountDeletion()
  }

  async function cancelAccountDeletion() {
    try {
      await meApi.meCancelAccountDeletion()
    } catch (e) {
      // 404: already cancelled elsewhere, so there is nothing pending any more.
      if (!(e instanceof ResponseError && e.response.status === 404)) throw e
    }
    accountDeletion.value = null
  }

  function markTermsUpdateRequired() {
    termsRefused.value = true
  }

  /**
   * Accepts the current terms (the version the user was shown). True on success. On a 409 (the terms changed meanwhile)
   * `termsError` holds the server's text and /me is reloaded so the screen shows the new version.
   */
  async function acceptTerms(): Promise<boolean> {
    const version = terms.value?.currentVersion
    if (version === undefined) return false
    termsError.value = null
    try {
      terms.value = await quietMeApi.meAcceptTerms({ acceptTermsRequest: { version } })
      termsRefused.value = false
      return true
    } catch (e) {
      logger.error(e, 'Failed to accept the terms')
      termsError.value = await readApiErrorMessage(e, TERMS_ERROR)
      const changed = e instanceof ResponseError && e.response.status === 409
      if (changed) await reloadTerms()
      return false
    }
  }

  // Refreshes only the terms from GET /me, leaving the rest of the stores alone.
  async function reloadTerms() {
    try {
      const response = await quietMeApi.meFetchMe()
      terms.value = response.terms ?? terms.value
    } catch (e) {
      logger.error(e, 'Failed to reload the terms')
    }
  }

  return {
    userProfile,
    terms,
    termsError,
    termsUpdateRequired,
    markTermsUpdateRequired,
    acceptTerms,
    accountDeletion,
    userPreferences,
    isProfileSetUp,
    isPreferencesSetUp,
    isRestricted,
    loadProfile,
    createProfile,
    updateProfile,
    savePreferences,
    requestAccountDeletion,
    cancelAccountDeletion
  }
})
